import { useState } from 'react'
import { useMScaleStore } from './store'
import { useBluetooth } from './hooks/useBluetooth'
import { ConnectionButton } from './components/Connection/ConnectionButton'
import { ConnectionStatus } from './components/Connection/ConnectionStatus'
import { EndUserView } from './views/EndUserView'
import { SettingsView } from './views/SettingsView'
import { Card } from './components/Common/Card'

function App() {
  const { isConnected, isSupported, disconnect } = useBluetooth()
  const [viewMode, setViewMode] = useState<'user' | 'settings'>('user')
  const isConnecting = useMScaleStore((state) => state.isConnecting)
  const connectionProgress = useMScaleStore((state) => state.connectionProgress)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-industrial-bg">
      {/* Full Screen Connection Overlay */}
      {isConnecting && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="flex flex-col items-center gap-6">
              {/* Spinner */}
              <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>

              {/* Text */}
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Connecting...
                </h2>
                {connectionProgress && (
                  <p className="text-gray-600 dark:text-gray-400">
                    {connectionProgress.step}
                    <br />
                    <span className="text-sm">
                      Step {connectionProgress.current} of {connectionProgress.total}
                    </span>
                  </p>
                )}
              </div>

              {/* Progress Bar */}
              {connectionProgress && (
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(connectionProgress.current / connectionProgress.total) * 100}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white dark:bg-industrial-surface shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            {/* Left: Title */}
            <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white hidden sm:block">
                M-Scale Manager
              </h1>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white sm:hidden">
                M-Scale
              </h1>
            </div>

            {/* Right: Connection status and navigation */}
            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <ConnectionStatus />
                  {viewMode === 'settings' ? (
                    <button
                      onClick={() => setViewMode('user')}
                      className="p-2 rounded-lg bg-gray-200 dark:bg-industrial-bg text-gray-700 dark:text-industrial-text-dim hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors"
                      title="Back to User View"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setViewMode('settings')}
                        className="p-2 rounded-lg bg-gray-200 dark:bg-industrial-bg text-gray-700 dark:text-industrial-text-dim hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors"
                        title="Settings"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                        </svg>
                      </button>
                      <button
                        onClick={disconnect}
                        className="p-2 rounded-lg bg-gray-200 dark:bg-industrial-bg text-gray-700 dark:text-industrial-text-dim hover:bg-gray-300 dark:hover:bg-gray-700 transition-colors"
                        title="Disconnect"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <ConnectionStatus />
                  <ConnectionButton />
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {!isSupported && (
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
          <div className="mb-6 p-4 sm:p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <h3 className="text-base sm:text-lg font-semibold text-red-900 dark:text-red-200 mb-2">
              Browser Not Supported
            </h3>
            <p className="text-xs sm:text-sm text-red-800 dark:text-red-300 mb-3">
              This application requires Web Bluetooth, which is only available in supported browsers over HTTPS.
            </p>
            <div className="text-xs sm:text-sm text-red-800 dark:text-red-300">
              <p className="font-semibold mb-1">Requirements:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Supported browsers: Chrome 100+, Edge 100+, or Opera 85+</li>
                <li>HTTPS connection (secure)</li>
                <li>Bluetooth enabled on your device</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {!isConnected ? (
        <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
          {/* Welcome Screen */}
          <Card title="Welcome to M-Scale Manager">
            <p className="text-sm sm:text-base text-gray-600 dark:text-industrial-text-dim mb-4 sm:mb-6">
              This application allows you to monitor and configure your M-Scale
              multi-head weighing system via Web Bluetooth.
            </p>

            <div className="p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                Getting Started
              </h3>
              <ol className="text-xs sm:text-sm text-blue-800 dark:text-blue-300 space-y-1 list-decimal list-inside">
                <li>Ensure your M-Scale device is powered on</li>
                <li>Click "Connect to M-Scale" button above</li>
                <li>Select your device from the browser's device picker (look for "M-Scale")</li>
                <li>Start monitoring your scale system</li>
              </ol>
            </div>
          </Card>
        </main>
      ) : (
        /* Connected - Show Selected View */
        viewMode === 'user' ? <EndUserView /> : <SettingsView />
      )}

      {/* Footer */}
      <footer className="mt-8 sm:mt-12 py-4 sm:py-6 border-t border-gray-200 dark:border-industrial-border">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 text-center text-xs sm:text-sm text-gray-500 dark:text-industrial-text-dim">
          <div>M-Scale Manager v1.2.1 • Copyright © 2026 Pulse Consulting LLC • All rights reserved</div>
          <div className="mt-2 space-x-4">
            <a href="/terms.html" target="_blank" className="hover:text-gray-700 dark:hover:text-gray-300 underline">
              Terms of Service
            </a>
            <span>•</span>
            <a href="/privacy.html" target="_blank" className="hover:text-gray-700 dark:hover:text-gray-300 underline">
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App
